import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface TaxComponentResult {
    id: string;
    name: string;
    category: 'indirect' | 'income' | 'social' | 'other';
    amount: number;
    rate: number;
}

interface TaxPresetSummary {
    id: string;
    name: string;
    country: string;
    entityType: 'individual' | 'company';
    description: string;
}

interface TaxBreakdownData {
    preset: TaxPresetSummary;
    grossRevenue: number;
    breakdown: TaxComponentResult[];
    totalsByCategory: Record<string, number>;
    totalTaxes: number;
    netIncome: number;
    effectiveRate: number;
    currency: string;
}

interface TaxBreakdownCardProps {
    data: TaxBreakdownData;
    formatCurrency: (amount: number, currency: string) => string;
}

const CATEGORY_LABELS: Record<string, string> = {
    indirect: 'Indirect Taxes',
    income: 'Income Tax',
    social: 'Social Contributions',
    other: 'Other Taxes',
};

const CATEGORY_COLORS: Record<string, string> = {
    indirect: '#8b5cf6',
    income: '#f59e0b',
    social: '#06b6d4',
    other: '#6b7280',
};

const TaxBreakdownCard = ({ data, formatCurrency }: TaxBreakdownCardProps) => {
    const categories = ['indirect', 'income', 'social', 'other'].filter(
        (cat) => (data.totalsByCategory[cat] ?? 0) > 0
    );

    return (
        <Card
            sx={{
                border: '3px solid #f59e0b',
                borderRadius: 0,
                backgroundColor: '#1a1a2e',
            }}
        >
            <CardContent sx={{ p: 3 }}>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        mb: 3,
                        borderBottom: '2px solid #f59e0b',
                        pb: 1,
                    }}
                >
                    <Box>
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 900,
                                textTransform: 'uppercase',
                                letterSpacing: '0.05em',
                            }}
                        >
                            Tax Breakdown
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: 'text.secondary', mt: 0.5 }}
                        >
                            {data.preset.name}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            px: 2,
                            py: 0.5,
                            border: '2px solid #f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#f59e0b' }}
                        >
                            {(data.effectiveRate * 100).toFixed(1)}% effective rate
                        </Typography>
                    </Box>
                </Box>

                {/* Summary Row */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
                        gap: 2,
                        mb: 3,
                    }}
                >
                    <Box
                        sx={{
                            p: 2,
                            border: '2px solid #f59e0b',
                            backgroundColor: 'rgba(245, 158, 11, 0.1)',
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                            Total Tax Liability
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 900, color: '#f59e0b' }}
                        >
                            {formatCurrency(data.totalTaxes, data.currency)}
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            p: 2,
                            border: '2px solid #22c55e',
                            backgroundColor: 'rgba(34, 197, 94, 0.1)',
                        }}
                    >
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                            Net After Taxes
                        </Typography>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 900, color: '#22c55e' }}
                        >
                            {formatCurrency(data.netIncome, data.currency)}
                        </Typography>
                    </Box>
                </Box>

                {/* Tax Categories */}
                <Stack spacing={2}>
                    {categories.map((category) => {
                        const categoryTotal = data.totalsByCategory[category] ?? 0;
                        const categoryComponents = data.breakdown.filter(
                            (c) => c.category === category
                        );

                        return (
                            <Box
                                key={category}
                                sx={{
                                    border: `2px solid ${CATEGORY_COLORS[category]}`,
                                    p: 2,
                                }}
                            >
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    alignItems="center"
                                    sx={{ mb: 1 }}
                                >
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.05em',
                                            color: CATEGORY_COLORS[category],
                                        }}
                                    >
                                        {CATEGORY_LABELS[category]}
                                    </Typography>
                                    <Typography
                                        variant="subtitle1"
                                        sx={{
                                            fontWeight: 900,
                                            color: CATEGORY_COLORS[category],
                                            fontFamily: 'monospace',
                                        }}
                                    >
                                        {formatCurrency(categoryTotal, data.currency)}
                                    </Typography>
                                </Stack>
                                <Stack spacing={0.5}>
                                    {categoryComponents.map((component) => (
                                        <Stack
                                            key={component.id}
                                            direction="row"
                                            justifyContent="space-between"
                                            alignItems="center"
                                            sx={{
                                                py: 0.5,
                                                borderBottom: '1px solid #374151',
                                                '&:last-child': { borderBottom: 'none' },
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{ color: 'text.secondary' }}
                                            >
                                                {component.name}{' '}
                                                <Typography
                                                    component="span"
                                                    variant="body2"
                                                    sx={{ color: 'text.disabled' }}
                                                >
                                                    ({(component.rate * 100).toFixed(1)}%)
                                                </Typography>
                                            </Typography>
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontFamily: 'monospace',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {formatCurrency(component.amount, data.currency)}
                                            </Typography>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Box>
                        );
                    })}
                </Stack>

                {/* Tax Reserve Progress */}
                <Box sx={{ p: 2, border: '2px solid #374151', mt: 3 }}>
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                    >
                        <Typography
                            variant="overline"
                            sx={{ color: 'text.secondary', fontWeight: 700 }}
                        >
                            Tax Reserve Progress
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ fontWeight: 700, color: '#f59e0b' }}
                        >
                            {(data.effectiveRate * 100).toFixed(1)}% of revenue
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={Math.min(data.effectiveRate * 100, 100)}
                        sx={{
                            height: 12,
                            borderRadius: 0,
                            backgroundColor: '#374151',
                            '& .MuiLinearProgress-bar': {
                                backgroundColor: '#f59e0b',
                                borderRadius: 0,
                            },
                        }}
                    />
                    <Typography
                        variant="body2"
                        sx={{ color: 'text.secondary', mt: 1, fontSize: '0.75rem' }}
                    >
                        Set aside this percentage of each booking to cover taxes
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

export default TaxBreakdownCard;
